/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: firewallRulesApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API client for Firewall Rules module
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type {
  FirewallRule,
  FirewallRuleFormData,
  FirewallRulesListResponse,
  FirewallService,
} from '../types';

/**
 * Fetch firewall rules list
 */
export async function fetchFirewallRules(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  action?: 'accept' | 'drop' | 'reject';
  direction?: 'input' | 'output' | 'forward';
  enabled?: boolean;
}): Promise<FirewallRulesListResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  if (params.search) {
    queryParams.append('search', params.search);
  }

  if (params.action) {
    queryParams.append('action', params.action);
  }

  if (params.direction) {
    queryParams.append('direction', params.direction);
  }

  if (params.enabled !== undefined) {
    queryParams.append('enabled', params.enabled.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/rules?${queryParams}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch firewall rules');
  }

  return response.json();
}

/**
 * Fetch firewall rule by ID
 */
export async function fetchFirewallRuleById(id: number): Promise<FirewallRule> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/rules/${id}`),
    {
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch firewall rule');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Create firewall rule
 */
export async function createFirewallRule(
  data: FirewallRuleFormData
): Promise<FirewallRule> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/rules'),
    {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create firewall rule');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Update firewall rule
 */
export async function updateFirewallRule(
  id: number,
  data: Partial<FirewallRuleFormData>
): Promise<FirewallRule> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/rules/${id}`),
    {
      method: 'PUT',
      headers: await getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update firewall rule');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Delete firewall rule
 */
export async function deleteFirewallRule(id: number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/admin/firewall/rules/${id}`),
    {
      method: 'DELETE',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete firewall rule');
  }
}

/**
 * Fetch available firewall services for selection
 */
export async function fetchFirewallServices(): Promise<FirewallService[]> {
  try {
    const response = await fetch(
      buildApiUrl('/api/admin/firewall/services?limit=100&sortBy=name&sortOrder=asc'),
      {
        headers: await getApiHeaders(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Failed to fetch firewall services:', response.status, response.statusText, errorData);
      return [];
    }

    const result = await response.json();

    if (result.success && Array.isArray(result.data)) {
      const servicesList = result.data
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((service: any) => service.enabled !== false) // Include all services, or only enabled if you prefer
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((service: any) => ({
          id: service.id,
          name: service.name,
          protocol: service.protocol,
          port: service.port,
        }));
      return servicesList;
    }


    return [];
  } catch (error) {
    console.error('Error fetching firewall services:', error);
    return [];
  }
}

/**
 * Apply Firewall rules to the system
 * This executes the nftables generator script on the server
 */
export async function applyFirewallRules(): Promise<{
  success: boolean;
  message: string;
  output?: string;
  warnings?: string | null;
}> {
  const response = await fetch(
    buildApiUrl('/api/admin/firewall/rules/apply'),
    {
      method: 'POST',
      headers: await getApiHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to apply firewall rules');
  }

  return response.json();
}
